import { useMemo } from "react";
import { get } from "lodash";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import {
  DEFAULT_HELP_MENU_LABEL,
  DEFAULT_HELP_MENU_HEADING,
  DEFAULT_HELP_MENU_DESCRIPTION,
  DEFAULT_HELP_MENU_SUPPORT_LINK_TEXT,
  DEFAULT_HELP_MENU_STATUS_LINK_TEXT,
  DEFAULT_SHOW_TEAMFORM_SUPPORT_REQUEST,
  DEFAULT_SHOW_CUSTOM_HELP_CONTENT,
  DEFAULT_SHOW_TEAMFORM_HELP_CONTENT,
  MAX_CUSTOM_HELP_LINKS,
} from "src/consts/helpMenuDefaults";

/**
 * Helper function to get config value or default, handling empty strings and null
 */
const getConfigValue = (helpConfig, key, defaultValue) => {
  const value = get(helpConfig, key);
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  return value;
};

const STRING_CONFIG_DEFAULTS = {
  helpMenuLabel: DEFAULT_HELP_MENU_LABEL,
  helpMenuHeading: DEFAULT_HELP_MENU_HEADING,
  helpMenuDescription: DEFAULT_HELP_MENU_DESCRIPTION,
  helpMenuSupportLinkText: DEFAULT_HELP_MENU_SUPPORT_LINK_TEXT,
  helpMenuStatusLinkText: DEFAULT_HELP_MENU_STATUS_LINK_TEXT,
};

const getBoolWithDefault = (value, defaultValue) =>
  value != null ? value : defaultValue;

/**
 * Hook to extract and provide help menu configuration from workspace
 * helpConfig is a JSON blob containing all help-related configuration
 * @returns {Object} Help menu configuration values
 */
export const useHelpMenuConfig = () => {
  const workspace = useWorkspace();
  const { enableCustomHelpAndLinks } = useWorkspaceFeatureFlags();

  const helpConfig = get(workspace, "config.helpConfig");
  const customHelpMenuTitle = get(helpConfig, "customHelpMenuTitle");
  const customHelpMenuDescription = get(
    helpConfig,
    "customHelpMenuDescription"
  );
  const showCustomHelpContentValue = get(helpConfig, "showCustomHelpContent");
  const showTeamFormSupportRequestValue = get(
    helpConfig,
    "showTeamFormSupportRequest"
  );
  const showTeamFormHelpContentValue = get(
    helpConfig,
    "showTeamFormHelpContent"
  );
  const externalContents = get(helpConfig, "externalContents");

  return useMemo(() => {
    if (!enableCustomHelpAndLinks) {
      return {
        ...STRING_CONFIG_DEFAULTS,
        showTeamFormSupportRequest: DEFAULT_SHOW_TEAMFORM_SUPPORT_REQUEST,
        showCustomHelpContent: getBoolWithDefault(
          showCustomHelpContentValue,
          DEFAULT_SHOW_CUSTOM_HELP_CONTENT
        ),
        showTeamFormHelpContent: getBoolWithDefault(
          showTeamFormHelpContentValue,
          DEFAULT_SHOW_TEAMFORM_HELP_CONTENT
        ),
        customHelpMenuTitle,
        customHelpMenuDescription,
        maxCustomHelpLinks: MAX_CUSTOM_HELP_LINKS,
        helpConfig,
      };
    }

    const hasTitle = Boolean(customHelpMenuTitle);
    const hasDescription = Boolean(customHelpMenuDescription);
    const hasExternalContents =
      Array.isArray(externalContents) && externalContents.length > 0;
    const hasCustomContentConfigured =
      hasTitle || hasDescription || hasExternalContents;

    const useDefaults = !hasCustomContentConfigured;

    const stringConfig = Object.fromEntries(
      Object.entries(STRING_CONFIG_DEFAULTS).map(([key, defaultValue]) => [
        key,
        useDefaults
          ? defaultValue
          : getConfigValue(helpConfig, key, defaultValue),
      ])
    );

    return {
      ...stringConfig,
      showTeamFormSupportRequest: useDefaults
        ? DEFAULT_SHOW_TEAMFORM_SUPPORT_REQUEST
        : getBoolWithDefault(
            showTeamFormSupportRequestValue,
            DEFAULT_SHOW_TEAMFORM_SUPPORT_REQUEST
          ),
      showCustomHelpContent: getBoolWithDefault(
        showCustomHelpContentValue,
        DEFAULT_SHOW_CUSTOM_HELP_CONTENT
      ),
      showTeamFormHelpContent: getBoolWithDefault(
        showTeamFormHelpContentValue,
        DEFAULT_SHOW_TEAMFORM_HELP_CONTENT
      ),
      customHelpMenuTitle,
      customHelpMenuDescription,
      maxCustomHelpLinks: MAX_CUSTOM_HELP_LINKS,
      helpConfig,
    };
  }, [
    helpConfig,
    enableCustomHelpAndLinks,
    customHelpMenuTitle,
    customHelpMenuDescription,
    showCustomHelpContentValue,
    showTeamFormHelpContentValue,
    showTeamFormSupportRequestValue,
    externalContents,
  ]);
};

export default useHelpMenuConfig;
